// internal/CommandBar.jsx — Commandes naturelles CEO (§32) : texte + vocal (Web Speech fr-FR), couche d'intentions déterministe JSONFIRST.
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mic, Send } from "lucide-react";
import { S, IT } from "@/internal/InternalLayout";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/internal`;
const H = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("civ_user_token") || ""}` });
const SUGGESTIONS = [
  "Qui n'a pas de backup ?",
  "Quelles tâches sont bloquées ?",
  "Désigne-moi comme backup d'Arnaud",
  "Retire l'accès d'Emmanuel aux données financières",
  "Cherche dans la knowledge base la procédure de déploiement",
];

export const CommandBar = ({ onActionDone }) => {
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [result, setResult] = useState(null);
  const recRef = useRef(null);

  const send = async (text) => {
    const t = (text || input).trim();
    if (!t || busy) return;
    setBusy(true); setResult(null); setInput("");
    try {
      const r = await fetch(`${API}/command`, { method: "POST", headers: H(), body: JSON.stringify({ text: t }) });
      const d = await r.json();
      if (!d.ok) throw new Error(d.message || d.error);
      if (d.type === "factory_redirect") {
        toast.info("Redirection vers la Workspace Factory…");
        navigate("/internal/workspaces", { state: { factoryIntent: d.factory_intent } });
        return;
      }
      setResult({ ...d, command: t });
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setBusy(false); }
  };

  const decide = async (id, decision) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/approvals/${id}/decide`, { method: "POST", headers: H(), body: JSON.stringify({ decision }) });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error);
      setResult((prev) => ({ ...prev, type: "answer", answer: decision === "approve" ? `✅ Exécuté : ${d.result}` : "Action rejetée — rien n'a été modifié.", items: [] }));
      if (decision === "approve") onActionDone?.();
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setBusy(false); }
  };

  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Reconnaissance vocale non supportée par ce navigateur (utilisez Chrome)."); return; }
    if (listening) { recRef.current?.stop(); return; }
    const rec = new SR();
    recRef.current = rec;
    rec.lang = "fr-FR"; rec.interimResults = false; rec.maxAlternatives = 1;
    rec.onstart = () => setListening(true);
    rec.onend = () => setListening(false);
    rec.onerror = () => { setListening(false); toast.error("Micro indisponible ou refusé."); };
    rec.onresult = (e) => {
      const t = e.results[0]?.[0]?.transcript || "";
      if (t) { setInput(t); send(t); }
    };
    rec.start();
  };

  return (
    <div style={{ ...S.card, padding: 16, marginBottom: 14, border: "1px solid rgba(46,226,123,0.3)" }} data-testid="ceo-command-bar">
      <h2 style={{ ...S.h2, color: IT.green, marginBottom: 4 }}>💬 Demander à AYA</h2>
      <div style={{ fontSize: 11.5, color: IT.muted, marginBottom: 10 }}>
        Commandes naturelles (texte ou vocal) — les questions répondent immédiatement, les actions sensibles passent par votre approbation (JSONFIRST).
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <input data-testid="ceo-command-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder='Ex : "Qui n’a pas de backup ?" · "Retire l’accès d’Emmanuel aux données financières"'
          style={{ flex: 1, background: "#0D1310", border: `1px solid ${IT.border}`, borderRadius: 12, padding: "11px 14px", color: IT.text, fontSize: 13, outline: "none", fontFamily: "inherit" }} />
        <button data-testid="ceo-command-mic" onClick={toggleVoice} title="Commande vocale"
          style={{ width: 44, borderRadius: 12, border: `1px solid ${listening ? "#F87171" : IT.border}`, background: listening ? "rgba(248,113,113,0.15)" : "rgba(255,255,255,0.04)", color: listening ? "#F87171" : IT.muted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Mic size={17} style={listening ? { animation: "pulse 1s infinite" } : {}} />
        </button>
        <button data-testid="ceo-command-send" onClick={() => send()} disabled={busy || !input.trim()} style={{ ...S.btn, opacity: busy ? 0.6 : 1 }}>
          <Send size={14} />
        </button>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
        {SUGGESTIONS.map((s) => (
          <button key={s} onClick={() => send(s)} data-testid="ceo-command-suggestion"
            style={{ padding: "5px 11px", borderRadius: 999, border: "1px solid rgba(139,163,148,0.3)", background: "rgba(255,255,255,0.03)", color: IT.muted, fontSize: 10.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            {s}
          </button>
        ))}
      </div>
      {busy && <div style={{ fontSize: 12, color: IT.muted, marginTop: 10 }}>⏳ AYA traite votre commande…</div>}
      {listening && <div style={{ fontSize: 12, color: "#F87171", marginTop: 10 }} data-testid="ceo-command-listening">🎙 Je vous écoute… parlez maintenant.</div>}
      {result && (
        <div style={{ marginTop: 12, padding: 13, borderRadius: 12, background: result.type === "approval_required" ? "rgba(232,184,75,0.07)" : "rgba(46,226,123,0.05)", border: `1px solid ${result.type === "approval_required" ? "rgba(232,184,75,0.35)" : "rgba(46,226,123,0.22)"}` }} data-testid="ceo-command-result">
          <div style={{ fontSize: 10.5, color: IT.muted, marginBottom: 5 }}>« {result.command} »</div>
          <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: "pre-wrap", lineHeight: 1.55 }}>{result.answer || result.question}</div>
          {(result.items || []).map((it, i) => (
            <div key={i} style={{ fontSize: 12.5, color: IT.text, padding: "4px 0 0 10px" }}>• {it}</div>
          ))}
          {result.type === "approval_required" && (
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button data-testid="ceo-command-reject" onClick={() => decide(result.approval_id, "reject")} disabled={busy}
                style={{ ...S.ghost, borderColor: "rgba(248,113,113,0.4)", color: "#F87171" }}>✕ Rejeter</button>
              <button data-testid="ceo-command-approve" onClick={() => decide(result.approval_id, "approve")} disabled={busy} style={S.btn}>✓ Confirmer & exécuter</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
